import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  App,
  Upload,
  Select,
  Switch,
  Row,
  Col,
  InputNumber,
  Modal,
} from "antd";
import {
  CloseOutlined,
  ExclamationCircleOutlined,
  PlusOutlined
} from "@ant-design/icons";
import API from "../../../services/api";
import { useCloudinaryUpload } from "../../../hooks/useCloudinaryUpload";
import { useState, useEffect } from "react";
import "./AddFranchise.css";
import { useNavigate } from "react-router-dom";
import LocationField from "../../LocationField";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CATEGORY_OPTIONS = [
  "food",
  "retail",
  "education",
  "fitness",
  "healthcare",
  "beauty",
  "technology",
  "other",
];

function AddFranchise() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);

  const {
    uploadedThumb,
    uploadedImages,
    imageFileList,
    setImageFileList,
    uploading,
    uploadThumbnail,
    uploadImages,
    removeImage
  } = useCloudinaryUpload("franchises", message);

  // Image upload errors
  const [thumbnailError, setThumbnailError] = useState(false);
  const [imagesError, setImagesError] = useState(false);

  // Custom category
  const [customCategory, setCustomCategory] = useState("");
  const [showCustomCategory, setShowCustomCategory] = useState(false);

  const [openDropdown, setOpenDropdown] = useState(null);

  const getStepStyle = (step) => ({
    display: current === step ? "block" : "none",
    width: "100%",
  });


  const handleBack = () => {
    const values = form.getFieldsValue();

    // Check if any field has value
    const hasData = Object.values(values).some(
      (val) =>
        val !== undefined &&
        val !== null &&
        val !== "" &&
        !(Array.isArray(val) && val.length === 0)
    );

    // ✅ If no data → go back directly
    if (!hasData) {
      window.history.back();
      return;
    }

    // ⚠️ If user entered something → show confirm
    Modal.confirm({
      title: "Leave this page?",
      icon: <ExclamationCircleOutlined />,
      content: "All your unsaved changes will be lost if you go back.",
      okText: "Yes, go back",
      cancelText: "Stay",
      okButtonProps: { danger: true },
      onOk: () => {
        window.history.back();
      },
    });
  };

  const steps = [
    "Brand",
    "Financial",
    "Location",
    "Contact",
    "Branches",
    "Images",
  ];

  const stepFields = [
    ["brandName", "category", "description"],
    ["investmentRequired", "franchiseFee", "royaltyFee", "areaRequired", "outletCount"],
    [
      "locality",
      "city",
      "state",
      "country",
      "pincode",
      "latitude",
      "longitude",
    ],
    ["contactName", "phone", "email", "website"],
    ["branches"],
    ["thumbnail", "images"],
  ];

  const next = async () => {
    // Special validation for category step (step 0)
    if (current === 0) {
      if (showCustomCategory && (!customCategory || customCategory.trim() === "")) {
        message.error("Please enter custom category");
        return;
      }
    }

    // Special validation for images step (step 5)
    if (current === 5) {
      if (!uploadedThumb) {
        setThumbnailError(true);
        message.error("Please upload a thumbnail image");
        return;
      }

      if (!uploadedImages || uploadedImages.length === 0) {
        setImagesError(true);
        message.error("Please upload at least one image");
        return;
      }

      setThumbnailError(false);
      setImagesError(false);


    }

    try {
      await form.validateFields(stepFields[current]);
      setCurrent(current + 1);
      window.scrollTo(0, 0);
    } catch (err) {
      const firstError =
        err.errorFields?.[0]?.errors?.[0] || "Please fill all required fields";
      message.error({
        content: firstError,
        key: "validation-error",
        duration: 2.5,
      });
    }
  };

  const prev = () => {
    setCurrent(current - 1);
    window.scrollTo(0, 0);
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);

      // Process branches
      const branches = values.branches
        ? values.branches
          .split("\n")
          .filter((line) => line.trim() !== "")
          .map((line) => {
            const [branchName, city, state] = line
              .split(",")
              .map((item) => item.trim());
            return { branchName, city, state };
          })
        : [];

      // Normalize website
      let normalizedWebsite = values.website || "";
      if (normalizedWebsite && !/^https?:\/\//i.test(normalizedWebsite)) {
        normalizedWebsite = `https://${normalizedWebsite}`;
      }

      const data = {
        title: values.brandName,
        description: values.description,
        franchiseDetails: {
          brandName: values.brandName,
          category: values.category,
          investmentRequired: Number(values.investmentRequired),
          franchiseFee: Number(values.franchiseFee || 0),
          royaltyFee: Number(values.royaltyFee || 0),
          areaRequired: Number(values.areaRequired || 0),
          outletCount: Number(values.outletCount || 1),
        },
        headquarters: {
          locality: values.locality,
          city: values.city,
          state: values.state,
          country: values.country,
          pincode: values.pincode,

          coordinates: {
            type: "Point",
            coordinates: [
              Number(values.longitude),
              Number(values.latitude),
            ],
          },
        },

        contactDetails: {
          contactName: values.contactName,
          email: values.email,
          phone: values.phone,
          website: normalizedWebsite,
        },
        branches,
      };



      await API.post("/franchises", {
        ...data,
        thumbnail: uploadedThumb?.url,
        images: uploadedImages.map(img => img.url),
      });

      navigate("/success", {
        state: { type: "franchise" }
      });
      setThumbnailError(false);
      setImagesError(false);
    } catch (err) {
      message.error(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const clickedDropdown = e.target.closest(
        ".fr-custom-select"
      );

      if (!clickedDropdown) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  return (
    <div className="fr-page">
      <div className="fr-container">
        <Card className="fr-card">
          {/* Card Header - Back button on right side */}
          <div className="fr-card-header">
            <div className="fr-header-left" />
            <div className="fr-header-center">
              <Title className="fr-title">Add Franchise</Title>
            </div>
            <div className="fr-header-right">
              <button className="fr-back-btn" onClick={handleBack} type="button">
                <CloseOutlined />
              </button>
            </div>
          </div>

          {/* Stepper */}
          <div className="fr-stepper">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`fr-step ${current === i ? "active" : ""} ${current > i ? "done" : ""}`}
              >
                <div className="fr-step-line-left" />
                <div className="fr-step-inner">
                  <div className="fr-circle">
                    {current > i ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </div>
                  <span className="fr-step-label">{step}</span>
                </div>
                <div className="fr-step-line-right" />
              </div>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="fr-progress-bar">
            <div
              className="fr-progress-fill"
              style={{ width: `${(current / (steps.length - 1)) * 100}%` }}
            />
          </div>
          <div className="fr-progress-label">
            Step {current + 1} of {steps.length} — {steps[current]}
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError

            className="fr-form"
          >
            {/* Step 0: Brand */}
            <div style={getStepStyle(0)}>
              <Form.Item
                name="brandName"
                label={
                  <span className="fr-label-with-badge">
                    <span className="fr-required-star">*</span>
                    Brand Name
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter your brand name" },
                  { min: 3, message: "Brand name must be at least 3 characters long" },
                  {
                    pattern: /^(?=.*[A-Za-z])[A-Za-z0-9][A-Za-z0-9\s.&'-]*$/,
                    message: "Brand name must start with a letter and can contain numbers, spaces, . & ' -",
                  },
                ]}
                help={null}
                validateStatus="success"
              >
                <Input placeholder="e.g. KFC, Pizza Hut, McDonald's" />
              </Form.Item>

              <Form.Item
                name="category"
                label={
                  <span className="fr-label-with-badge">
                    <span className="fr-required-star">*</span>
                    Category
                  </span>
                }
                required
                rules={[{ required: true, message: "Please select a category" }]}
              >
                <div className="fr-custom-select">
                  <div
                    className="fr-custom-select-trigger"
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === "category"
                          ? null
                          : "category"
                      )
                    }
                  >
                    {form.getFieldValue("category")
                      ? form
                        .getFieldValue("category")
                        .charAt(0)
                        .toUpperCase() +
                      form
                        .getFieldValue("category")
                        .slice(1)
                      : "Select category"}

                    <span className="fr-custom-arrow">
                      ⌄
                    </span>
                  </div>

                  {openDropdown === "category" && (
                    <div className="fr-custom-dropdown">
                      {CATEGORY_OPTIONS.map((item) => (
                        <div
                          key={item}
                          className="fr-custom-dropdown-item"
                          onClick={() => {
                            form.setFieldValue(
                              "category",
                              item
                            );

                            if (item === "other") {
                              setShowCustomCategory(true);
                            } else {
                              setShowCustomCategory(false);
                              setCustomCategory("");
                            }

                            setOpenDropdown(null);
                          }}
                        >
                          {item.charAt(0).toUpperCase() +
                            item.slice(1)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Form.Item>
              {showCustomCategory && (
                <Form.Item
                  name="customCategory"
                  label="Enter Custom Category"
                  rules={[
                    { required: true, message: "Please enter custom category" },
                    { min: 2, message: "Category must be at least 2 characters" },
                    { pattern: /^[A-Za-z][A-Za-z\s&-]*$/, message: "Start with letter, can include letters, spaces, & and -" }
                  ]}
                  style={{ marginTop: -8 }}
                >
                  <Input
                    placeholder="e.g. Restaurant, Café, Fitness"
                    value={customCategory}
                    onChange={(e) => {
                      setCustomCategory(e.target.value);
                      form.setFieldValue("category", e.target.value);
                    }}
                  />
                </Form.Item>
              )}

              <Form.Item
                name="description"
                label={
                  <span className="fr-label-with-badge">
                    <span className="fr-required-star">*</span>
                    Description
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please provide a description" },
                  { min: 20, message: "Description should be at least 20 characters" },
                ]}
                help={null}
                validateStatus="success"
              >
                <TextArea rows={5} placeholder="Describe the franchise model, opportunity, support system, training programs, and market fit..." />
              </Form.Item>
            </div>

            {/* Step 1: Financial */}
            <div style={getStepStyle(1)}>
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="investmentRequired"
                    label={
                      <span className="fr-label-with-badge">
                        <span className="fr-required-star">*</span>
                        Investment Required (₹)
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter investment amount" },
                      { type: "number", min: 10000, message: "Investment must be at least ₹10,000" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 500000" min={10000} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="franchiseFee"
                    label={
                      <span className="fr-label-with-badge">
                        <span className="fr-required-star">*</span>
                        Franchise Fee (₹)
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter franchise fee" },
                      { type: "number", min: 0, message: "Franchise fee cannot be negative" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 100000" min={0} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="royaltyFee"
                    label={
                      <span className="fr-label-with-badge">
                        <span className="fr-required-star">*</span>
                        Royalty Fee (%)
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter royalty fee" },
                      { type: "number", min: 0, max: 100, message: "Enter valid percentage (0-100)" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 5" min={0} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="areaRequired"
                    label={
                      <span className="fr-label-with-badge">
                        <span className="fr-required-star">*</span>
                        Area Required (sq ft)
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter area required" },
                      { type: "number", min: 50, message: "Area must be at least 50 sq ft" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 500" min={50} />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="outletCount"
                    label={
                      <span className="fr-label-with-badge">
                        <span className="fr-required-star">*</span>
                        Outlet Count
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter outlet count" },
                      { type: "number", min: 1, message: "Outlet count must be at least 1" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <InputNumber style={{ width: "100%" }} placeholder="e.g. 10" min={1} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Step 2: Location */}
            <div style={getStepStyle(2)}>
              <Form.Item
                name="locality"
                label={
                  <span className="fr-label-with-badge">
                    <span className="fr-required-star">*</span>
                    Locality
                  </span>
                }
                required
                rules={[
                  {
                    required: true,
                    message: "Please select locality",
                  },

                  {
                    pattern:
                      /^(?=.*[A-Za-z])[A-Za-z0-9\s.,#&()'/-]+$/,
                    message: "Invalid locality",
                  },
                ]}
              >
                <LocationField
                  value={form.getFieldValue("locality")}
                  form={form}
                  message={message}
                  setAutoDetecting={setAutoDetecting}
                  onChange={(value) => {
                    form.setFieldsValue({
                      locality: value,
                    });
                  }}
                />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="city"
                    label={
                      <span className="fr-label-with-badge">
                        <span className="fr-required-star">*</span>
                        City
                      </span>
                    }
                    required
                    rules={[
                      {
                        required: true,
                        message: "Please enter the city",
                      },

                      {
                        pattern: /^[A-Za-z\s.-]+$/,
                        message:
                          "City name should contain only letters",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="pincode"
                    label={
                      <span className="fr-label-with-badge">
                        <span className="fr-required-star">*</span>
                        Pincode
                      </span>
                    }
                    required
                    rules={[
                      {
                        required: true,
                        message: "Please enter pincode",
                      },

                      {
                        pattern: /^[0-9]{4,10}$/,
                        message: "Invalid pincode",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="state"
                    label={
                      <span className="fr-label-with-badge">
                        <span className="fr-required-star">*</span>
                        State
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the State" },
                      { pattern: /^[A-Za-z\s.-]+$/, message: "State name should contain only letters" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input placeholder="e.g. Karnataka" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="country"
                    label={
                      <span className="fr-label-with-badge">
                        <span className="fr-required-star">*</span>
                        Country
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the country" },
                      { pattern: /^[A-Za-z\s.-]+$/, message: "Country name should contain only letters" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input placeholder="e.g. India" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="latitude"
                    label={
                      <span className="fr-label-with-badge">
                        <span className="fr-required-star">*</span>
                        Latitude
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Latitude is required" },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === "")
                            return Promise.reject("Latitude is required");
                          const str = value.toString();
                          if ((str.match(/\./g) || []).length > 1)
                            return Promise.reject("Only one decimal point allowed");
                          const parts = str.split(".");
                          if (parts[1] && parts[1].length > 6)
                            return Promise.reject("Max 6 digits allowed after decimal");
                          if (typeof value !== "number" || Number.isNaN(value))
                            return Promise.reject("Enter a valid latitude");
                          if (value < -90 || value > 90)
                            return Promise.reject("Latitude must be between -90 and 90");
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={-90}
                      max={90}
                      step={0.000001}
                      placeholder="e.g. 12.971891"
                      onBlur={() => {
                        const val = form.getFieldValue("latitude");
                        if (typeof val === "number") {
                          form.setFieldsValue({ latitude: Number(val.toFixed(6)) });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="longitude"
                    label={
                      <span className="fr-label-with-badge">
                        <span className="fr-required-star">*</span>
                        Longitude
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Longitude is required" },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === "")
                            return Promise.reject("Longitude is required");
                          const str = value.toString();
                          if ((str.match(/\./g) || []).length > 1)
                            return Promise.reject("Only one decimal point allowed");
                          const parts = str.split(".");
                          if (parts[1] && parts[1].length > 6)
                            return Promise.reject("Max 6 digits allowed after decimal");
                          if (typeof value !== "number" || Number.isNaN(value))
                            return Promise.reject("Enter a valid longitude");
                          if (value < -180 || value > 180)
                            return Promise.reject("Longitude must be between -180 and 180");
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={-180}
                      max={180}
                      step={0.000001}
                      placeholder="e.g. 77.594563"
                      onBlur={() => {
                        const val = form.getFieldValue("longitude");
                        if (typeof val === "number") {
                          form.setFieldsValue({ longitude: Number(val.toFixed(6)) });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Step 3: Contact */}
            <div style={getStepStyle(3)}>
              <Form.Item
                name="contactName"
                label={
                  <span className="fr-label-with-badge">
                    <span className="fr-required-star">*</span>
                    Contact Person Name
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter contact name" },
                  { pattern: /^[A-Za-z\s.-]+$/, message: "Name can only contain letters and basic characters" },
                ]}
                help={null}
                validateStatus="success"
              >
                <Input placeholder="e.g. Rajesh Kumar" />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="phone"
                    label={
                      <span className="fr-label-with-badge">
                        <span className="fr-required-star">*</span>
                        Phone Number
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter a phone number" },
                      { pattern: /^[6-9]\d{9}$/, message: "Enter a valid 10-digit Indian phone number" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input maxLength={10} placeholder="e.g. 9876543210" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="email"
                    label={
                      <span className="fr-label-with-badge">
                        <span className="fr-required-star">*</span>
                        Email Address
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter your email address" },
                      { type: "email", message: "Enter a valid email address" },
                    ]}
                    help={null}
                    validateStatus="success"
                  >
                    <Input placeholder="e.g. franchise@brand.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="website"
                label="Website"
                rules={[
                  {
                    pattern: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i,
                    message: "Enter a valid website URL",
                  },
                ]}
                help={null}
                validateStatus="success"
              >
                <Input placeholder="e.g. www.yourbrand.com" />
              </Form.Item>
            </div>

            {/* Step 4: Branches */}
            <div style={getStepStyle(4)}>
              <Form.Item
                name="branches"
                label="Branches"
                extra="Format: Branch Name, City, State (one per line)"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || !value.trim()) return Promise.resolve();

                      const lines = value.split("\n").filter(l => l.trim());
                      if (lines.length === 0) return Promise.resolve();

                      for (let line of lines) {
                        const parts = line.split(",").map(p => p.trim());
                        if (parts.length !== 3) {
                          return Promise.reject("Each line must have exactly 3 parts: Branch Name, City, State");
                        }
                        const [branch, city, state] = parts;
                        if (!branch || !city || !state) {
                          return Promise.reject("Branch Name, City, and State are all required");
                        }
                        if (!/^[A-Za-z0-9\s.&'-]+$/.test(branch) ||
                          !/^[A-Za-z\s.-]+$/.test(city) ||
                          !/^[A-Za-z\s.-]+$/.test(state)) {
                          return Promise.reject("Invalid characters detected. Use only letters, numbers, spaces, . & ' -");
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                help={null}
                validateStatus="success"
              >
                <TextArea rows={5} placeholder="Koramangala, Bengaluru, Karnataka&#10;Indiranagar, Bengaluru, Karnataka&#10;Whitefield, Bengaluru, Karnataka" />
              </Form.Item>
            </div>

            {/* Step 5: Images */}
            <div style={getStepStyle(5)}>
              <Form.Item

                name="thumbnail"
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList}
                label={
                  <span className="fr-label-with-badge">
                    <span className="fr-required-star">*</span>
                    Thumbnail
                    <span className="fr-badge-primary">Main Image</span>
                  </span>
                }

                validateStatus={thumbnailError ? "error" : ""}
                help={thumbnailError ? "Please upload a thumbnail image" : ""}
              >
                <Upload.Dragger
                  maxCount={1}
                  accept="image/png,image/jpeg"
                  className="fr-upload"
                  beforeUpload={(file) => {
                    uploadThumbnail(file);

                    // ✅ ADD THIS (VERY IMPORTANT)
                    form.setFieldsValue({
                      thumbnail: [
                        {
                          uid: file.uid,
                          name: file.name,
                          status: "done",
                        },
                      ],
                    });

                    return false;
                  }}
                  onChange={() => {
                    setThumbnailError(false);
                  }}
                >
                  <div className="fr-upload-inner">
                    <p className="fr-upload-title">Upload Thumbnail</p>
                    <p className="fr-upload-hint">This is the first image users see · PNG, JPEG</p>
                  </div>
                </Upload.Dragger>
              </Form.Item>

              <Form.Item
                name="images"
                label={
                  <span className="fr-label-with-badge">
                    <span className="fr-required-star">*</span>
                    Franchise Images
                    <span className="fr-badge-secondary">Up to 5</span>
                  </span>
                }
                validateStatus={imagesError ? "error" : ""}
                help={imagesError ? "Please upload at least one image" : ""}
              >
                <Upload.Dragger
                  fileList={imageFileList}
                  multiple
                  accept="image/png,image/jpeg"
                  className="fr-upload"
                  beforeUpload={(file) => {

                    const isValidType = ["image/png", "image/jpeg"].includes(file.type);

                    if (!isValidType) {
                      message.error("Only PNG and JPEG files are allowed");
                      return Upload.LIST_IGNORE;
                    }

                    // prevent upload beyond limit
                    if (imageFileList.length >= 5) {
                      message.error("Maximum 5 images allowed");
                      return Upload.LIST_IGNORE;
                    }

                    uploadImages([file]);

                    setImagesError(false);

                    return false;
                  }}
                  onRemove={(file) => {
                    removeImage(file.public_id);
                  }}
                  itemRender={(originNode) => originNode}
                >
                  {imageFileList.length >= 5 ? null : (
                    <div className="fr-upload-inner">

                      <p className="fr-upload-title">
                        Upload Franchise Images
                      </p>

                      <p className="fr-upload-hint">
                        Drag & drop images here or click to browse
                      </p>

                      <p className="fr-upload-hint">
                        PNG, JPEG · Maximum 5 images
                      </p>
                    </div>
                  )}
                </Upload.Dragger>
              </Form.Item>
            </div>

            {/* Navigation Buttons */}
            <div className="fr-btns">
              {current > 0 && (
                <Button
                  onClick={prev}
                  className="fr-btn-prev"
                  disabled={autoDetecting}
                >
                  ← Previous
                </Button>
              )}
              <div className="fr-btns-right">
                {current < steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={next}
                    className="fr-btn-next"
                    disabled={autoDetecting}
                  >
                    Next →
                  </Button>
                )}
                {current === steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={async () => {

                      // ✅ ADD THIS
                      if (uploading) {
                        message.error("Please wait until images finish uploading");
                        return;
                      }

                      if (!uploadedThumb) {
                        setThumbnailError(true);
                        message.error("Please upload a thumbnail image");
                        return;
                      }

                      if (!uploadedImages || uploadedImages.length === 0) {
                        setImagesError(true);
                        message.error("Please upload at least one image");
                        return;
                      }

                      setThumbnailError(false);
                      setImagesError(false);

                      form.submit();
                    }}
                    loading={loading}
                    disabled={loading || uploading}
                    className="fr-btn-submit"
                  >
                    Submit Listing
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
}

export default AddFranchise;